import { Skeleton, Tab, Tabs } from '@mui/material';
import { useId } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@mui/material/styles';
import { BenchmarkGrid } from '@strength/domain';
import { MdAdd } from '@teamworksdev/icons';
import { CoreExercise } from '../utils';
import {
  AthleteExerciseBenchmarkCell,
  AthleteExerciseBenchmarkRow,
  BenchmarksDataTable,
} from './BenchmarksDataTable';
import { KpiBenchmarksDataTable } from './KpiBenchmarksDataTable';
import type {
  AthleteKpiBenchmarkRow,
  KpiGridMetric,
  RecordAthleteKpiBenchmarkCell,
} from './kpi-benchmarks-utils';

type BenchmarksTableProps = {
  data: AthleteExerciseBenchmarkRow[];
  benchmarkGrid?: BenchmarkGrid;
  benchmarkGrids: BenchmarkGrid[];
  coreExercises: CoreExercise[];
  onSetAthleteId: (profileId: string) => void;
  handleTabChange: (_event: React.SyntheticEvent, newValue: string) => void;
  /**
   * Whether the viewer holds `teams.benchmarks.edit`. Gates the add-grid tab
   * and the grid edit affordances; reads stay available without it.
   */
  canEdit?: boolean;
  onSaveBenchmarkValueCell: (
    cell: AthleteExerciseBenchmarkCell,
    onSuccess: () => void,
  ) => void;
  onOpenEditBenchmarkGrid: () => void;
  /**
   * KPI path: when `isKpiGrid` is true the selected tab renders the KPI grid
   * (metric columns) instead of the rep exercise grid. The remaining `kpi*`
   * props supply its data/metrics/record callback and are unused otherwise.
   */
  isKpiGrid?: boolean;
  kpiData?: AthleteKpiBenchmarkRow[];
  kpiMetrics?: KpiGridMetric[];
  onRecordKpiValueCell?: (
    cell: RecordAthleteKpiBenchmarkCell,
    onSuccess: () => void,
    onError: () => void,
  ) => void;
};

export const BenchmarksTable = ({
  data,
  benchmarkGrid,
  benchmarkGrids,
  coreExercises,
  onSetAthleteId,
  handleTabChange,
  onSaveBenchmarkValueCell,
  onOpenEditBenchmarkGrid,
  isKpiGrid = false,
  kpiData = [],
  kpiMetrics = [],
  onRecordKpiValueCell,
  canEdit = false,
}: BenchmarksTableProps) => {
  const { t } = useTranslation();
  const addGridReasonId = useId();
  const {
    palette: { greyscaleLight },
  } = useTheme();
  return (
    <div className="w-full h-full flex flex-col overflow-hidden relative">
      <Tabs
        value={benchmarkGrid?.id}
        onChange={handleTabChange}
        className="bg-gray-100 border-none"
        sx={{
          display: 'flex',
          '& .MuiTabs-indicator': {
            display: 'none',
          },
          '& .MuiTabs-flexContainer': {
            display: 'flex',
            width: '100%',
          },
        }}
      >
        {benchmarkGrids.map((grid) => (
          <Tab
            key={grid.id}
            value={grid.id}
            label={grid.name}
            className="border-r-[1px] border-b-[1px] border-gray-200"
            sx={{
              '&.Mui-selected': {
                backgroundColor: 'white',
                borderBottom: 'none',
              },
            }}
          />
        ))}
        <Tab
          value="add"
          disabled={!canEdit}
          aria-describedby={canEdit ? undefined : addGridReasonId}
          label={
            <div
              style={{
                display: 'flex',
                justifyContent: 'flex-start',
                width: '100%',
              }}
            >
              <MdAdd size={16} />
            </div>
          }
          sx={{
            flex: '1 1 auto',
            minWidth: 0,
            maxWidth: 'none',
            textAlign: 'left',
            paddingLeft: '16px',
            borderBottom: `1px solid ${greyscaleLight[200]}`,
            '& .MuiTab-label': {
              width: '100%',
              textAlign: 'left',
            },
            '&.Mui-selected': {
              backgroundColor: 'white',
              borderBottom: `1px solid ${greyscaleLight[200]} !important`,
            },
          }}
        />
      </Tabs>
      {/* The disabled add-grid tab's explanation is associated
          programmatically (not hover-only). It sits outside <Tabs> because
          MUI treats non-Tab children as tabs; this keeps only Tab elements
          as direct children. */}
      {!canEdit && (
        <span id={addGridReasonId} className="sr-only">
          {t('permissionDisabled.edit', { item: t('benchmarks') })}
        </span>
      )}

      {benchmarkGrid ? (
        isKpiGrid ? (
          <KpiBenchmarksDataTable
            data={kpiData}
            metrics={kpiMetrics}
            benchmarkGridId={benchmarkGrid.id}
            onSetAthleteId={onSetAthleteId}
            onRecordKpiValueCell={onRecordKpiValueCell ?? (() => undefined)}
            onOpenEditBenchmarkGrid={onOpenEditBenchmarkGrid}
            canEdit={canEdit}
          />
        ) : (
          <BenchmarksDataTable
            data={data}
            coreExercises={coreExercises}
            onSetAthleteId={onSetAthleteId}
            onSaveBenchmarkValueCell={onSaveBenchmarkValueCell}
            onOpenEditBenchmarkGrid={onOpenEditBenchmarkGrid}
            benchmarkGridId={benchmarkGrid.id}
            canEdit={canEdit}
          />
        )
      ) : (
        <>
          {/** Show a loading state if the tabs are still loading */}
          <Skeleton variant="rectangular" height={300} />
        </>
      )}
    </div>
  );
};
